import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  TextField,
  Paper,
  Container,
  InputAdornment,
  IconButton,
  Grid,
  MenuItem,
  Select,
  FormControl,
  Snackbar,
  Alert,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import axios from '../../../axios-order';
import {
  DataGridPro,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid-pro';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const theme = createTheme({
  palette: {
    primary: {
      main: '#3366FF',
    },
    background: {
      default: '#F8F9FA',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: 'none',
        },
      },
    },
  },
});

// Helper functions
const formatDate = (date) => {
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const toISODateString = (date) => {
  return date.toISOString().split('T')[0];
};

const formatDateISO = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toSnakeCase = (str) => {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => (index === 0 ? word.toLowerCase() : `_${word.toLowerCase()}`))
    .replace(/\s+/g, '');
};

const transformCardDetails = (cardDetails) => {
  const transformed = {};
  Object.keys(cardDetails).forEach((key) => {
    const snakeCaseKey = toSnakeCase(key);
    transformed[snakeCaseKey] = cardDetails[key];
  });
  return transformed;
};

// Transform data for E-Invoice to match Einvoicecolumns and secondInvoiceColumns
const transformEInvoiceData = (data, isCategoryDetails = true) => {
  return data.map((item) => {
    if (isCategoryDetails) {
      return {
        id: item.id || Math.random(), // Ensure unique ID
        category: item.service || item.category || 'Unknown',
        generated_count: item.invoice_count || item.generated_count || 0,
        generated_amount: item.total_invoice_value || item.generated_amount || 0,
        pending_count: item.pending_count || 0,
        pending_amount: item.pending_amount || 0,
        cancelled_count: item.cancelled_count || 0,
        cancelled_amount: item.cancelled_amount || 0,
      };
    } else {
      return {
        id: item.id || Math.random(), // Ensure unique ID
        invoice_type: item.invoice_type || 'Unknown',
        generated_count: item.invoice_count || item.generated_count || 0,
        generated_amount: item.total_invoice_value || item.generated_amount || 0,
        pending_count: item.pending_count || 0,
        pending_amount: item.pending_amount || 0,
        cancelled_count: item.cancelled_count || 0,
        cancelled_amount: item.cancelled_amount || 0,
      };
    }
  });
};

const Dashboard = () => {
  const [dateRange, setDateRange] = useState('today');
  const [startDate, setStartDate] = useState(new Date('2025-04-01'));
  const [endDate, setEndDate] = useState(new Date('2025-04-23'));
  const [invoiceType, setInvoiceType] = useState('Tax Invoice');
  const [showReport, setShowReport] = useState(false);
  const [category, setCategory] = useState('Data Grid View');
  const [cardDetails, setCardDetails] = useState(null);
  const [categoryDetails, setCategoryDetails] = useState([]);
  const [invoiceTypeDetails, setInvoiceTypeDetails] = useState([]);
  const [dashboardType, setDashboardType] = useState('');
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('info');
  const [open, setOpen] = useState(false);
  const [dashboardDataLoading, setDashboardDataLoading] = useState(false);

  const handleRangeChange = (range) => {
    setDateRange(range);
    const today = new Date();
    let newStartDate, newEndDate;

    switch (range) {
      case 'today':
        newStartDate = today;
        newEndDate = today;
        break;
      case 'week':
        newStartDate = new Date(today);
        newStartDate.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
        newEndDate = new Date(newStartDate);
        newEndDate.setDate(newStartDate.getDate() + 6);
        break;
      case 'month':
        newStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
        newEndDate = new Date(today);
        break;
      case 'custom':
        newStartDate = startDate;
        newEndDate = endDate;
        break;
      default:
        return;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleInvoiceTypeChange = (type) => {
    setInvoiceType(type);
  };

  const handleStartDateChange = (e) => {
    const newDate = new Date(e.target.value);
    if (!isNaN(newDate)) {
      setStartDate(newDate);
    }
  };

  const handleEndDateChange = (e) => {
    const newDate = new Date(e.target.value);
    if (!isNaN(newDate)) {
      setEndDate(newDate);
    }
  };

  const handleCloseSnackbar = () => {
    setOpen(false);
  };

  const onSubmitHandler = async () => {
    try {
      setDashboardDataLoading(true);
      let fromDate = formatDateISO(startDate);
      let toDate = formatDateISO(endDate);

      if (!fromDate || !toDate) {
        if (!dateRange || dateRange === 'today') {
          const today = new Date();
          fromDate = formatDateISO(today);
          toDate = formatDateISO(today);
          setStartDate(today);
          setEndDate(today);
        }
      }

      const business_entity_id = localStorage.getItem('business_entity_id');

      const payload = {
        from_date: fromDate,
        to_date: toDate,
        business_entity_id: business_entity_id,
        invoice_type: invoiceType,
      };

      setCardDetails(null);
      setCategoryDetails([]);
      setInvoiceTypeDetails([]);
      setShowReport(false);

      axios
        .post('/einvoice/dashboard_einvoice', payload)
        .then((response) => {
          const { card_details, dashboard_type, category_details, invoice_type_details } = response.data;

          const transformedCardDetails = transformCardDetails(card_details);

          // Transform data for E-Invoice
          const transformedCategoryDetails =
            invoiceType === 'E-Invoice'
              ? transformEInvoiceData(category_details, true)
              : category_details;
          const transformedInvoiceTypeDetails =
            invoiceType === 'E-Invoice'
              ? transformEInvoiceData(invoice_type_details, false)
              : invoice_type_details;

          setCardDetails(transformedCardDetails);
          setDashboardType(dashboard_type);
          setCategoryDetails(transformedCategoryDetails);
          setInvoiceTypeDetails(transformedInvoiceTypeDetails);
          setShowReport(true);
          setCategory('Data Grid View');

          setMessage('Report generated successfully!');
          setSeverity('success');
          setOpen(true);
          setDashboardDataLoading(false);
        })
        .catch((error) => {
          setDashboardDataLoading(false);
          const errorMessage = error.response?.data?.message || 'An error occurred';
          setMessage(errorMessage);
          setSeverity('error');
          setOpen(true);
        });
    } catch (error) {
      setDashboardDataLoading(false);
      const errorMessage = error.response?.data?.message || 'An error occurred';
      setMessage(errorMessage);
      setSeverity('error');
      setOpen(true);
    }
  };

  const handleGenerateReport = () => {
    onSubmitHandler();
  };

  const handleCategoryChange = (event) => {
    setCategory(event.target.value);
  };

  const columns = [
    {
      field: 'service',
      headerName: 'Category',
      flex: 1,
      minWidth: 150,
      align: 'left',
      filterable: true,
    },
    {
      field: 'invoice_count',
      headerName: 'Invoice Count',
      flex: 1,
      minWidth: 200,
      align: 'left',
      filterable: true,
    },
    {
      field: 'total_taxable_value',
      headerName: 'Total Taxable Value',
      flex: 0.7,
      minWidth: 120,
      pinned: 'left',
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
    {
      field: 'total_igst',
      headerName: 'Total IGST',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
    {
      field: 'total_cgst',
      headerName: 'Total CGST',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
    {
      field: 'total_sgst',
      headerName: 'Total SGST',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
    {
      field: 'total_cess',
      headerName: 'Total Cess',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
    {
      field: 'total_tax_value',
      headerName: 'Total Tax Value',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
    {
      field: 'total_invoice_value',
      headerName: 'Total Invoice Value',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
  ];

  const secondColumns = [
    {
      field: 'invoice_type',
      headerName: 'Invoice Type',
      flex: 1,
      minWidth: 150,
      align: 'left',
      filterable: true,
    },
    {
      field: 'invoice_count',
      headerName: 'Invoice Count',
      flex: 1,
      minWidth: 200,
      align: 'left',
      filterable: true,
    },
    {
      field: 'total_taxable_value',
      headerName: 'Total Taxable Value',
      flex: 0.7,
      minWidth: 120,
      pinned: 'left',
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
    {
      field: 'total_igst',
      headerName: 'Total IGST',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
    {
      field: 'total_cgst',
      headerName: 'Total CGST',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
    {
      field: 'total_sgst',
      headerName: 'Total SGST',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
    {
      field: 'total_cess',
      headerName: 'Total Cess',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
    {
      field: 'total_tax_value',
      headerName: 'Total Tax Value',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
    {
      field: 'total_invoice_value',
      headerName: 'Total Invoice Value',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
  ];

  const Einvoicecolumns = [
    {
      field: 'category',
      headerName: 'Category',
      flex: 1,
      minWidth: 150,
      align: 'left',
      filterable: true,
    },
    {
      field: 'generated_count',
      headerName: 'Generated Count',
      flex: 1,
      minWidth: 200,
      align: 'left',
      filterable: true,
    },
    {
      field: 'generated_amount',
      headerName: 'Generated Amount',
      flex: 0.7,
      minWidth: 120,
      pinned: 'left',
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
    {
      field: 'pending_count',
      headerName: 'Pending Count',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
    },
    {
      field: 'pending_amount',
      headerName: 'Pending Amount',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
    {
      field: 'cancelled_count',
      headerName: 'Cancelled Count',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
    },
    {
      field: 'cancelled_amount',
      headerName: 'Cancelled Amount',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
  ];

  const secondInvoiceColumns = [
    {
      field: 'invoice_type',
      headerName: 'Invoice Type',
      flex: 1,
      minWidth: 150,
      align: 'left',
      filterable: true,
    },
    {
      field: 'generated_count',
      headerName: 'Generated Count',
      flex: 1,
      minWidth: 200,
      align: 'left',
      filterable: true,
    },
    {
      field: 'generated_amount',
      headerName: 'Generated Amount',
      flex: 0.7,
      minWidth: 120,
      pinned: 'left',
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
    {
      field: 'pending_count',
      headerName: 'Pending Count',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
    },
    {
      field: 'pending_amount',
      headerName: 'Pending Amount',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
    {
      field: 'cancelled_count',
      headerName: 'Cancelled Count',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
    },
    {
      field: 'cancelled_amount',
      headerName: 'Cancelled Amount',
      flex: 1,
      minWidth: 180,
      align: 'left',
      filterable: true,
      valueFormatter: (params) => `₹${new Intl.NumberFormat('en-IN').format(params.value)}`,
    },
  ];

  const CustomToolbar = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        height: '60px',
        flexWrap: 'wrap',
        padding: '3px 10px',
        backgroundColor: '#ffffff',
        borderRadius: '4px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1 }}>
        <GridToolbarColumnsButton
          sx={{
            borderColor: '#1976d2',
            color: '#1976d2',
            borderRadius: '4px',
            padding: '2px 4px',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: '#e3f2fd',
            },
          }}
        />
        <GridToolbarFilterButton
          sx={{
            borderColor: '#1976d2',
            color: '#1976d2',
            borderRadius: '4px',
            padding: '2px 4px',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: '#e3f2fd',
            },
          }}
        />
        <GridToolbarDensitySelector
          sx={{
            borderColor: '#1976d2',
            color: '#1976d2',
            borderRadius: '4px',
            padding: '2px 4px',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: '#e3f2fd',
            },
          }}
        />
      </Box>
    </Box>
  );

  // Chart Configurations
  const categoryChartOptions = {
    chart: {
      type: 'bar',
      marginTop: 20,
      height: 400, // Set fixed height for better visibility
    },
    title: {
      text: 'Category Details',
      y: 10,
      margin: 5,
    },
    subtitle: {
      text: 'Invoice Metrics by Service Category',
      y: 25,
    },
    xAxis: {
      categories:
        invoiceType === 'Tax Invoice'
          ? categoryDetails.map((item) => item.service)
          : categoryDetails.map((item) => item.category),
      title: {
        text: null,
      },
      gridLineWidth: 1,
      lineWidth: 0,
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Value (₹)',
        align: 'high',
      },
      labels: {
        overflow: 'justify',
        formatter: function () {
          return Highcharts.numberFormat(this.value, 0, '', ',');
        },
      },
      gridLineWidth: 0,
    },
    tooltip: {
      valueSuffix: '',
    },
    plotOptions: {
      bar: {
        borderRadius: 0,
        dataLabels: {
          enabled: true,
          formatter: function () {
            return Highcharts.numberFormat(this.y, 0, '', ',');
          },
        },
        groupPadding: 0.1,
      },
    },
    legend: {
      layout: 'vertical',
      align: 'right',
      verticalAlign: 'top',
      x: -40,
      y: 40,
      floating: true,
      borderWidth: 1,
      backgroundColor: Highcharts.defaultOptions.legend.backgroundColor || '#FFFFFF',
      shadow: true,
      symbolRadius: 50,
    },
    credits: {
      enabled: false,
    },
    series:
      invoiceType === 'Tax Invoice'
        ? [
            {
              name: 'Invoice Count',
              data: categoryDetails.map((item) => item.invoice_count),
              color: '#ADD8E6',
            },
            {
              name: 'Total Taxable Value',
              data: categoryDetails.map((item) => item.total_taxable_value),
              color: '#800080',
            },
            {
              name: 'Total Invoice Value',
              data: categoryDetails.map((item) => item.total_invoice_value),
              color: '#90EE90',
            },
          ]
        : [
            {
              name: 'Generated Count',
              data: categoryDetails.map((item) => item.generated_count),
              color: '#ADD8E6',
            },
            {
              name: 'Generated Amount',
              data: categoryDetails.map((item) => item.generated_amount),
              color: '#800080',
            },
            {
              name: 'Pending Amount',
              data: categoryDetails.map((item) => item.pending_amount),
              color: '#90EE90',
            },
          ],
  };

  const invoiceTypeChartOptions = {
    chart: {
      type: 'bar',
      marginTop: 20,
      height: 400, // Set fixed height for better visibility
    },
    title: {
      text: 'Invoice Type Details',
      y: 10,
      margin: 5,
    },
    subtitle: {
      text: 'Invoice Metrics by Invoice Type',
      y: 25,
    },
    xAxis: {
      categories: invoiceTypeDetails.map((item) => item.invoice_type),
      title: {
        text: null,
      },
      gridLineWidth: 1,
      lineWidth: 0,
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Value (₹)',
        align: 'high',
      },
      labels: {
        overflow: 'justify',
        formatter: function () {
          return Highcharts.numberFormat(this.value, 0, '', ',');
        },
      },
      gridLineWidth: 0,
    },
    tooltip: {
      valueSuffix: '',
    },
    plotOptions: {
      bar: {
        borderRadius: 0,
        dataLabels: {
          enabled: true,
          formatter: function () {
            return Highcharts.numberFormat(this.y, 0, '', ',');
          },
        },
        groupPadding: 0.1,
      },
    },
    legend: {
      layout: 'vertical',
      align: 'right',
      verticalAlign: 'top',
      x: -40,
      y: 40,
      floating: true,
      borderWidth: 1,
      backgroundColor: Highcharts.defaultOptions.legend.backgroundColor || '#FFFFFF',
      shadow: true,
      symbolRadius: 50,
    },
    credits: {
      enabled: false,
    },
    series:
      invoiceType === 'Tax Invoice'
        ? [
            {
              name: 'Invoice Count',
              data: invoiceTypeDetails.map((item) => item.invoice_count),
              color: '#ADD8E6',
            },
            {
              name: 'Total Taxable Value',
              data: invoiceTypeDetails.map((item) => item.total_taxable_value),
              color: '#800080',
            },
            {
              name: 'Total Invoice Value',
              data: invoiceTypeDetails.map((item) => item.total_invoice_value),
              color: '#90EE90',
            },
          ]
        : [
            {
              name: 'Generated Count',
              data: invoiceTypeDetails.map((item) => item.generated_count),
              color: '#ADD8E6',
            },
            {
              name: 'Generated Amount',
              data: invoiceTypeDetails.map((item) => item.generated_amount),
              color: '#800080',
            },
            {
              name: 'Pending Amount',
              data: invoiceTypeDetails.map((item) => item.pending_amount),
              color: '#90EE90',
            },
          ],
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4, bgcolor: 'white' }}>
          <ButtonGroup
            variant="outlined"
            aria-label="invoice type button group"
            sx={{ mb: 2, '& .MuiButton-root': { px: 3, py: 1 } }}
          >
            <Button
              onClick={() => handleInvoiceTypeChange('Tax Invoice')}
              variant={invoiceType === 'Tax Invoice' ? 'contained' : 'outlined'}
            >
              Tax Invoice
            </Button>
            <Button
              onClick={() => handleInvoiceTypeChange('E-Invoice')}
              variant={invoiceType === 'E-Invoice' ? 'contained' : 'outlined'}
            >
              E-Invoice
            </Button>
          </ButtonGroup>
          <Typography variant="h5" fontWeight="600" mb={3}>
            {invoiceType === 'Tax Invoice' ? 'Tax Invoice Dashboard' : 'E-Invoice Dashboard'}
          </Typography>

          <ButtonGroup
            variant="outlined"
            aria-label="date range button group"
            sx={{ mb: 3, '& .MuiButton-root': { px: 3, py: 1 } }}
          >
            <Button
              onClick={() => handleRangeChange('today')}
              variant={dateRange === 'today' ? 'contained' : 'outlined'}
            >
              Today
            </Button>
            <Button
              onClick={() => handleRangeChange('week')}
              variant={dateRange === 'week' ? 'contained' : 'outlined'}
            >
              This Week
            </Button>
            <Button
              onClick={() => handleRangeChange('month')}
              variant={dateRange === 'month' ? 'contained' : 'outlined'}
            >
              This Month
            </Button>
            <Button
              onClick={() => handleRangeChange('custom')}
              variant={dateRange === 'custom' ? 'contained' : 'outlined'}
            >
              Custom Range
            </Button>
          </ButtonGroup>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Start Date
              </Typography>
              {dateRange === 'custom' ? (
                <TextField
                  type="date"
                  value={toISODateString(startDate)}
                  onChange={handleStartDateChange}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CalendarTodayOutlinedIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" fontWeight="bold" mb={0}>
                        {startDate.getDate().toString().padStart(2, '0')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {startDate.toLocaleString('en-GB', { month: 'long', year: 'numeric' })}
                      </Typography>
                    </Box>
                    <CalendarTodayOutlinedIcon color="action" />
                  </Box>
                </Paper>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowForwardIcon color="action" />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" mb={1}>
                End Date
              </Typography>
              {dateRange === 'custom' ? (
                <TextField
                  type="date"
                  value={toISODateString(endDate)}
                  onChange={handleEndDateChange}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CalendarTodayOutlinedIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" fontWeight="bold" mb={0}>
                        {endDate.getDate().toString().padStart(2, '0')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {endDate.toLocaleString('en-GB', { month: 'long', year: 'numeric' })}
                      </Typography>
                    </Box>
                    <CalendarTodayOutlinedIcon color="action" />
                  </Box>
                </Paper>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<DescriptionOutlinedIcon />}
              sx={{ px: 4, py: 1.5 }}
              onClick={handleGenerateReport}
            >
              Generate Report
            </Button>
          </Box>
        </Paper>

        {/* Report Section */}
        {showReport && (
          <Paper sx={{ pt: 2, px: 4, pb: 4, bgcolor: 'white' }}>
            {/* Common Dropdown */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 2 }}>
              <FormControl sx={{ minWidth: 150 }}>
                <Select
                  value={category}
                  onChange={handleCategoryChange}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                >
                  <MenuItem value="Data Grid View">Data Grid</MenuItem>
                  <MenuItem value="Data Graph View">Data Graph</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Conditional Rendering Based on Dropdown */}
            {category === 'Data Grid View' ? (
              <>
                {/* Category Details Table */}
                <Box sx={{ height: '65vh', width: '100%', mb: 4 }}>
                  <DataGridPro
                    rows={categoryDetails.map((row, index) => ({ id: row.id || index, ...row }))}
                    columns={invoiceType === 'Tax Invoice' ? columns : Einvoicecolumns}
                    loading={dashboardDataLoading}
                    localeText={{
                      toolbarDensity: 'Size',
                      toolbarDensityLabel: 'Size',
                      toolbarDensityCompact: 'Small',
                      toolbarDensityStandard: 'Medium',
                      toolbarDensityComfortable: 'Large',
                    }}
                    sx={{
                      maxHeight: '65vh',
                      '& .MuiDataGrid-columnHeaders': {
                        color: '#fff',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        textAlign: 'right',
                        zIndex: 1000,
                      },
                      '& .MuiDataGrid-columnHeader': {
                        position: 'relative',
                        textAlign: 'right',
                        width: '100%',
                        fontWeight: 'bold',
                        backgroundColor: '#607d8b',
                      },
                      '& .MuiDataGrid-columnHeaderTitle': {
                        color: '#fff',
                      },
                      '& .MuiDataGrid-sortIcon': {
                        opacity: 'inherit !important',
                      },
                      '& .MuiDataGrid-iconButtonContainer': {
                        visibility: 'visible',
                      },
                      '& .MuiDataGrid-menuIconButton': {
                        opacity: '1 !important',
                        visibility: 'visible !important',
                        display: 'flex !important',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        '& .MuiDataGrid-columnHeader': {
                          '& .MuiDataGrid-menuIconButton': {
                            display: 'flex !important',
                            visibility: 'visible !important',
                            opacity: '1 !important',
                          },
                        },
                      },
                      '& .MuiDataGrid-columnHeaderIcon': {
                        color: '#fff',
                      },
                      '& .MuiDataGrid-columnHeader .MuiIconButton-root': {
                        color: '#fff',
                      },
                      '& .MuiDataGrid-row:nth-of-type(odd)': {
                        backgroundColor: '#f9f9f9',
                      },
                      '& .MuiDataGrid-row:nth-of-type(even)': {
                        backgroundColor: '#ffffff',
                      },
                      '& .MuiDataGrid-cell': {
                        padding: '8px',
                        textAlign: 'center',
                      },
                      '& .MuiDataGrid-footerContainer': {
                        backgroundColor: '#f0f0f0',
                      },
                      '& .MuiCheckbox-root': {
                        color: '#1976d2',
                      },
                      '& .MuiDataGrid-row:hover': {
                        backgroundColor: '#d3e3fc',
                        cursor: 'pointer',
                      },
                    }}
                    pagination
                    pageSizeOptions={[5, 10, 25, 50]}
                    components={{
                      Toolbar: CustomToolbar,
                    }}
                  />
                </Box>

                {/* Invoice Type Details Table */}
                <Box sx={{ height: '65vh', width: '100%' }}>
                  <DataGridPro
                    rows={invoiceTypeDetails.map((row, index) => ({ id: row.id || index, ...row }))}
                    columns={invoiceType === 'Tax Invoice' ? secondColumns : secondInvoiceColumns}
                    loading={dashboardDataLoading}
                    localeText={{
                      toolbarDensity: 'Size',
                      toolbarDensityLabel: 'Size',
                      toolbarDensityCompact: 'Small',
                      toolbarDensityStandard: 'Medium',
                      toolbarDensityComfortable: 'Large',
                    }}
                    sx={{
                      maxHeight: '65vh',
                      '& .MuiDataGrid-columnHeaders': {
                        color: '#fff',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        textAlign: 'right',
                        zIndex: 1000,
                      },
                      '& .MuiDataGrid-columnHeader': {
                        position: 'relative',
                        textAlign: 'right',
                        width: '100%',
                        fontWeight: 'bold',
                        backgroundColor: '#607d8b',
                      },
                      '& .MuiDataGrid-columnHeaderTitle': {
                        color: '#fff',
                      },
                      '& .MuiDataGrid-sortIcon': {
                        opacity: 'inherit !important',
                      },
                      '& .MuiDataGrid-iconButtonContainer': {
                        visibility: 'visible',
                      },
                      '& .MuiDataGrid-menuIconButton': {
                        opacity: '1 !important',
                        visibility: 'visible !important',
                        display: 'flex !important',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        '& .MuiDataGrid-columnHeader': {
                          '& .MuiDataGrid-menuIconButton': {
                            display: 'flex !important',
                            visibility: 'visible !important',
                            opacity: '1 !important',
                          },
                        },
                      },
                      '& .MuiDataGrid-columnHeaderIcon': {
                        color: '#fff',
                      },
                      '& .MuiDataGrid-columnHeader .MuiIconButton-root': {
                        color: '#fff',
                      },
                      '& .MuiDataGrid-row:nth-of-type(odd)': {
                        backgroundColor: '#f9f9f9',
                      },
                      '& .MuiDataGrid-row:nth-of-type(even)': {
                        backgroundColor: '#ffffff',
                      },
                      '& .MuiDataGrid-cell': {
                        padding: '8px',
                        textAlign: 'center',
                      },
                      '& .MuiDataGrid-footerContainer': {
                        backgroundColor: '#f0f0f0',
                      },
                      '& .MuiCheckbox-root': {
                        color: '#1976d2',
                      },
                      '& .MuiDataGrid-row:hover': {
                        backgroundColor: '#d3e3fc',
                        cursor: 'pointer',
                      },
                    }}
                    pagination
                    pageSizeOptions={[5, 10, 25, 50]}
                    components={{
                      Toolbar: CustomToolbar,
                    }}
                  />
                </Box>
              </>
            ) : (
              /* Data Graph View (Highcharts Bar Charts) */
              <Box>
                {/* Category Details Chart */}
                <Box sx={{ mb: 2 }}>
                  <div id="container1" style={{ width: '100%' }}></div>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={categoryChartOptions}
                    containerProps={{ id: 'container1' }}
                  />
                </Box>

                {/* Invoice Type Details Chart */}
                <Box>
                  <div id="container2" style={{ width: '100%' }}></div>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={invoiceTypeChartOptions}
                    containerProps={{ id: 'container2' }}
                  />
                </Box>
              </Box>
            )}
          </Paper>
        )}

        <Snackbar open={open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
          <Alert onClose={handleCloseSnackbar} severity={severity} sx={{ width: '100%' }}>
            {message}
          </Alert>
        </Snackbar>
      </Container>
    </ThemeProvider>
  );
};

export default Dashboard;